import type { Location, TeamsShift, TeamsTimecard } from '../api/types';

/** How far a clock-in may drift from a shift's start and still count as that shift. */
const MATCH_TOLERANCE_MS = 3 * 60 * 60 * 1000;

/**
 * Finds the scheduled shift a timecard was worked against: same employee, same
 * location, and the shift whose time overlaps the punch most. With no overlap,
 * falls back to the shift starting nearest the clock-in, within tolerance.
 */
export function matchShift(card: TeamsTimecard, shifts: TeamsShift[]): TeamsShift | undefined {
  if (!card.clockIn) return undefined;
  const inMs  = new Date(card.clockIn.dateTime).getTime();
  const outMs = card.clockOut ? new Date(card.clockOut.dateTime).getTime() : inMs;

  let best: TeamsShift | undefined;
  let bestOverlap = 0;
  let bestGap = Infinity;

  for (const s of shifts) {
    if (s.entraObjectId !== card.entraObjectId || s.locationId !== card.locationId) continue;
    if (!s.sharedStart || !s.sharedEnd) continue;
    const startMs = new Date(s.sharedStart).getTime();
    const endMs   = new Date(s.sharedEnd).getTime();

    const overlap = Math.min(outMs, endMs) - Math.max(inMs, startMs);
    if (overlap > bestOverlap) {
      best = s;
      bestOverlap = overlap;
      continue;
    }
    if (bestOverlap > 0) continue;

    const gap = Math.abs(startMs - inMs);
    if (gap <= MATCH_TOLERANCE_MS && gap < bestGap) {
      best = s;
      bestGap = gap;
    }
  }

  return best;
}

/** Worked minutes minus scheduled minutes; null when either side is incomplete. */
export function scheduleDiffMinutes(card: TeamsTimecard, shift: TeamsShift | undefined): number | null {
  if (!shift?.sharedStart || !shift.sharedEnd || !card.clockIn || !card.clockOut) return null;
  const worked    = new Date(card.clockOut.dateTime).getTime() - new Date(card.clockIn.dateTime).getTime();
  const scheduled = new Date(shift.sharedEnd).getTime() - new Date(shift.sharedStart).getTime();
  return Math.round((worked - scheduled) / 60_000);
}

export type ShiftHalf = 'am' | 'pm';

/** Local timestamp for HH:MM on the same local calendar day as `day`. */
function atLocalTime(day: Date, hhmm: string): number {
  const [h = 0, m = 0] = hhmm.split(':').map(Number);
  const d = new Date(day);
  d.setHours(h, m, 0, 0);
  return d.getTime();
}

/**
 * Classifies a punch as AM or PM by which of the location's shift windows it
 * overlaps more (the same windows tip pools use). A punch outside both windows
 * falls to AM if it starts before the AM window ends, otherwise PM.
 */
export function classifyShiftHalf(
  card: TeamsTimecard,
  windows: Pick<Location, 'amShiftStart' | 'amShiftEnd' | 'pmShiftStart' | 'pmShiftEnd'>,
): ShiftHalf {
  if (!card.clockIn) return 'pm';
  const inDate = new Date(card.clockIn.dateTime);
  const inMs   = inDate.getTime();
  const outMs  = card.clockOut ? new Date(card.clockOut.dateTime).getTime() : Date.now();

  const overlap = (start: string, end: string) =>
    Math.max(0, Math.min(outMs, atLocalTime(inDate, end)) - Math.max(inMs, atLocalTime(inDate, start)));

  const am = overlap(windows.amShiftStart, windows.amShiftEnd);
  const pm = overlap(windows.pmShiftStart, windows.pmShiftEnd);
  if (am === 0 && pm === 0) return inMs < atLocalTime(inDate, windows.amShiftEnd) ? 'am' : 'pm';
  return am > pm ? 'am' : 'pm';
}
